import React from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";

import type {
  IDeployChangeItem,
  IDeployNote,
  IImpact,
  IModule,
  IReleaseType,
} from "@multiconsult-group/multiplacer-core";
import { usePostMultiplacerReleaseNotesDeployNotesMutation } from "../../../openapi";
import {
  RELEASE_IMPACT_OPTIONS,
  RELEASE_MODULE_OPTIONS,
  RELEASE_TYPE_OPTIONS,
} from "../releaseNotesUiSpecs";

type ICreateDeployNote = Omit<IDeployNote, "_id" | "publishedInReleaseId">;

const createEmptyItem = (): IDeployChangeItem => ({
  userFacing: true,
  title: "", // 👈 NYTT
  module: "CEILING",
  type: "changed",
  impact: "medium",
  area: "UI",
  text: "",
});

export const DeployNoteCreateCard: React.FC = () => {
  const [postDeployNote, { isLoading, isError, error, isSuccess }] =
    usePostMultiplacerReleaseNotesDeployNotesMutation();

  const form = useForm<ICreateDeployNote>({
    initialValues: {
      mergedAt: new Date().toISOString(),
      gitSha: "",
      pr: undefined,
      internalNote: "",
      changeItems: [createEmptyItem()],
    },
    validate: {
      mergedAt: (v) => (v?.trim() ? null : "Påkrevd"),

      // ✅ Robust validering for array-items (title/area/text per item)
      changeItems: (items) => {
        const errors: any[] = [];

        (items ?? []).forEach((it, idx) => {
          const e: any = {};

          if (!it?.title?.trim()) e.title = "Påkrevd";
          if (!it?.area?.trim()) e.area = "Påkrevd";
          if (!it?.text?.trim()) e.text = "Påkrevd";

          errors[idx] = Object.keys(e).length ? e : null;
        });

        return errors.some(Boolean) ? errors : null;
      },
    },
  });

  const addItem = () => form.insertListItem("changeItems", createEmptyItem());
  const removeItem = (idx: number) => form.removeListItem("changeItems", idx);

  const handleSubmit = async (values: ICreateDeployNote) => {
    const payload: ICreateDeployNote = {
      mergedAt: values.mergedAt,
      gitSha: values.gitSha?.trim() ? values.gitSha.trim() : undefined,
      pr:
        values.pr?.id != null || (values.pr?.title?.trim() ?? "") !== ""
          ? {
              id: values.pr?.id,
              title: values.pr?.title?.trim() ? values.pr?.title.trim() : undefined,
            }
          : undefined,
      internalNote: values.internalNote?.trim() ? values.internalNote.trim() : undefined,

      changeItems: (values.changeItems ?? []).map((it) => ({
        userFacing: it.userFacing,
        title: it.title.trim(), // 👈 NYTT
        module: it.module,
        type: it.type,
        impact: it.impact,
        area: it.area.trim(),
        text: it.text.trim(),
        media: it.media, // hvis du ikke har UI for media nå, lar vi den bare være med/undefined
      })),
    };

    try {
      await postDeployNote({ body: payload }).unwrap();
      form.reset();
    } catch (e) {
      // Handled by isError
    }
  };

  return (
    <Card withBorder radius="lg" p="md">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Group justify="space-between" align="center">
            <Title order={3}>Deploy-notat</Title>
            <Badge variant="light">Admin</Badge>
          </Group>

          <Text size="sm" c="dimmed">
            Logg en deploy etter merge. Senere samler vi flere deploy-notater til en release-notat
            for sluttbrukere.
          </Text>

          {isError && (
            <Alert color="red" title="Kunne ikke lagre" variant="light">
              {(error as any)?.data?.message || "Det oppstod en feil under lagring."}
            </Alert>
          )}

          {isSuccess && <Alert color="green">Lagret!</Alert>}

          <Divider label="Metadata" />

          <TextInput
            label="MergedAt (ISO)"
            placeholder="2026-02-09T12:34:56.000Z"
            {...form.getInputProps("mergedAt")}
          />

          <TextInput
            label="Git SHA (valgfritt)"
            placeholder="abc1234"
            {...form.getInputProps("gitSha")}
          />

          <Group grow>
            <TextInput
              label="PR id (valgfritt)"
              placeholder="412"
              value={form.values.pr?.id?.toString() ?? ""}
              onChange={(e) => {
                const raw = e.currentTarget.value.trim();
                const id = raw ? Number(raw) : undefined;
                form.setFieldValue("pr", {
                  ...(form.values.pr ?? {}),
                  id: Number.isFinite(id) ? id : undefined,
                });
              }}
            />

            <TextInput
              label="PR tittel (valgfritt)"
              placeholder="Raskere zoom i PixBimViewer"
              value={form.values.pr?.title ?? ""}
              onChange={(e) => {
                const title = e.currentTarget.value;
                form.setFieldValue("pr", { ...(form.values.pr ?? {}), title });
              }}
            />
          </Group>

          <Textarea
            label="Intern note (valgfritt)"
            placeholder="Kort intern beskrivelse..."
            autosize
            minRows={2}
            {...form.getInputProps("internalNote")}
          />

          <Divider label="Endringer" />

          <Stack gap="md">
            {form.values.changeItems.map((_, idx) => {
              const base = `changeItems.${idx}` as const;

              return (
                <Card key={idx} withBorder radius="md" p="md">
                  <Stack>
                    <Group justify="space-between" align="center">
                      <Group gap="xs">
                        <Badge variant="light">#{idx + 1}</Badge>
                        <Checkbox
                          label="Synlig for sluttbruker"
                          checked={form.values.changeItems[idx]?.userFacing ?? false}
                          onChange={(e) =>
                            form.setFieldValue(`${base}.userFacing`, e.currentTarget.checked)
                          }
                        />
                      </Group>

                      <Button
                        variant="subtle"
                        color="red"
                        onClick={() => removeItem(idx)}
                        disabled={form.values.changeItems.length <= 1}
                      >
                        Fjern
                      </Button>
                    </Group>

                    {/* 👇 NYTT: Title for change item */}
                    <TextInput
                      label="Tittel"
                      placeholder="Kort oppsummering (f.eks. 'Raskere zoom i viewer')"
                      {...form.getInputProps(`${base}.title`)}
                    />

                    <Group grow>
                      <Select
                        label="Modul"
                        data={RELEASE_MODULE_OPTIONS}
                        value={form.values.changeItems[idx]?.module}
                        onChange={(v) =>
                          form.setFieldValue(`${base}.module`, (v ?? "CEILING") as IModule)
                        }
                        allowDeselect={false}
                      />

                      <Select
                        label="Type"
                        data={RELEASE_TYPE_OPTIONS}
                        value={form.values.changeItems[idx]?.type}
                        onChange={(v) =>
                          form.setFieldValue(`${base}.type`, (v ?? "changed") as IReleaseType)
                        }
                        allowDeselect={false}
                      />

                      <Select
                        label="Impact (valgfritt)"
                        data={RELEASE_IMPACT_OPTIONS}
                        value={form.values.changeItems[idx]?.impact ?? null}
                        onChange={(v) =>
                          form.setFieldValue(
                            `${base}.impact`,
                            (v ?? undefined) as IImpact | undefined
                          )
                        }
                        clearable
                      />
                    </Group>

                    <Group grow>
                      <TextInput
                        label="Area"
                        placeholder="UI / Ytelse / Validering"
                        {...form.getInputProps(`${base}.area`)}
                      />

                      <TextInput
                        label="Tekst (brukerorientert)"
                        placeholder="Raskere zoom og panorering i store tegninger."
                        {...form.getInputProps(`${base}.text`)}
                      />
                    </Group>
                  </Stack>
                </Card>
              );
            })}
          </Stack>

          <Group justify="space-between">
            <Button variant="light" onClick={addItem}>
              Legg til endring
            </Button>

            <Button type="submit" loading={isLoading}>
              Lagre deploy-notat
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
};
